/**
 * @author        david.loehr@zumper.com (David Loehr)
 * @copyright     Copyright (c) 2023, Zumper
 * @description   PageAddressMultiUnit.tsx
 */

import { getRootRelativeUrlFromFiltersAndLocation } from '@zcode-ui/filters'
import { useRouteProps } from '@zcode-ui/hook'
import { guessMapDimensions, selectMapDimensions } from '@zcode-ui/map'
import {
  selectActiveListings,
  selectAddress,
  selectInactiveListings,
  selectListingId,
} from '@zcode-ui/perm-address'
import { selectEntity } from '@zcode-ui/web-zumper-global-state'
import {
  AddressPageContext,
  type AddressPageContextType,
  PageMultiUnitAddressRoot,
} from '@zcode-ui/web-zumper-pages'
import type { AutocompleteResult } from '@zcode/bizos'
import { AnalyticsC, keepBetaParam, locationDescriptorToString } from '@zcode/nr-utils'
import {
  hasHasGalleryParam,
  selectDisplayMedia,
} from '@zcode/nr/es/shared/bizos/rentable/selectors'
import {
  deleteHash,
  pushSearch,
  replaceSearch,
} from '@zcode/nr/es/shared/packages/history-middleware'
import { Size, getImagePlaceholder as _getImagePlaceholder } from '@zcode/nr/es/shared/utils'
import type { State } from '@zcode/state-type'
import { routesConstants } from '@zcode/zumper-routes'
import React, { forwardRef, useCallback, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { AppBannerContainer } from '../../components/AppBanner/AppBannerContainer'
import { useLoadGoogleAds } from '../../components/GoogleAd/hooks'
import { ManageTitle } from '../../components/ManageTitle/ManageTitle'
import { useStreamChannel } from '../../components/MessageButton/useStreamChannel'
import { InactiveSaveSearchSection as InactiveSaveSearchSectionNZR } from '../../components/SaveSearch/InactiveSaveSearchSection/InactiveSaveSearchSection'
import { NotifyMeButtonContainer as NotifyMeButton } from '../../components/SaveSearch/InactiveSaveSearchSection/NotifyMeButtonContainer'
import { DetailAds } from '../../components/detail/DetailAds/DetailAds'
import { FavoriteContainer as NzrFavoriteContainer } from '../../components/favorite/FavoriteContainer'
import { selectShouldLimitLeads } from '../../components/message/MessageForm/helpers/getShouldLimitLeads'
import { NavbarContainer as NavbarNZR } from '../../components/navbar/NavbarContainer'
import { getEntityIsMessaged, getMessaged } from '../../inspectors/index'
import navigateToAutocompleteResult from '../../modules/autocomplete/thunks/navigateToAutocompleteResult'
import { mixpanelEvent } from '../../modules/mixpanel/thunks/mixpanelEvent'
import { useDispatch } from '../../modules/useDispatch'
import { getHoodAndCityLinks } from '../../pages/PageLTRDetail/helpers/getHoodAndCityLinks'
import { default as preloadRoutes } from '../../routes/preload'
import { MAPBOX_BROWSER_KEY } from '../../settings/settings'
import { type AddressPageTitleParams, addressPageTitle } from '../../utils/page'
import { AddressMessageButton } from './AddressMessageButton'
import { AddressTourButton } from './AddressTourButton'
import { BottomHalf } from './BottomHalf/BottomHalf'
import { NZRUnitsTableLazyLoadableSection } from './NZRUnitsTableLazyLoadableSection'

const DetailAppBanner = () => <AppBannerContainer type="DETAIL" />

const FavoriteContainer = (props: Parameters<AddressPageContextType['FavoriteContainer']>[0]) => {
  return <NzrFavoriteContainer analyticChannel={AnalyticsC.PERMANENT_ADDRESS} {...props} />
}
const InactiveSaveSearchSection = (
  props: Parameters<AddressPageContextType['InactiveSaveSearchSection']>[0]
) => {
  return (
    <InactiveSaveSearchSectionNZR text="Alert me when similar rentals are available." {...props} />
  )
}

const Navbar = () => {
  return <NavbarNZR isStatic={false} origin={AnalyticsC.PERMANENT_ADDRESS} />
}
const getImagePlaceholder = (groupId: number) => _getImagePlaceholder(groupId, Size.small)

const selectAddressTitleProps = (state: State) => {
  const entity = selectEntity(state)
  const activeListings = selectActiveListings(state)
  const inactiveListings = selectInactiveListings(state)
  const totalListings = activeListings.length + inactiveListings.length
  const isSingleListing = totalListings === 1
  const firstListing = activeListings[0] ?? inactiveListings[0]
  return {
    entity,
    firstListing,
    formattedAddress: selectAddress(state).formattedAddress ?? '',
    isSingleListing,
    totalListings,
  } satisfies AddressPageTitleParams
}

export const PageAddressMultiUnit = () => {
  const entity = useSelector(selectEntity) ?? {}
  const mapDimensions = useSelector(selectMapDimensions)
  const hoodAndCityLinks = getHoodAndCityLinks(entity, true)
  const titleProps = useSelector(selectAddressTitleProps)
  const routeProps = useRouteProps()
  const hash = selectListingId({ hashOnly: true, routeProps })

  const shouldLimitLeads = useSelector(selectShouldLimitLeads)
  const { createHref, location, replace } = useHistory()
  const dispatch = useDispatch()
  useLoadGoogleAds()
  const messaged = useSelector(getMessaged)
  const selectIsMessaged = useCallback(
    (entity: Parameters<typeof getEntityIsMessaged>[1]) => {
      return getEntityIsMessaged(messaged, entity)
    },
    [messaged]
  )
  const handleBackBtnClick = useCallback(
    (result: AutocompleteResult) => {
      const { height, width } = guessMapDimensions(mapDimensions)
      void dispatch(navigateToAutocompleteResult({ result, height, width }))
    },
    [dispatch, mapDimensions]
  )

  useEffect(() => {
    if (hasHasGalleryParam(location.search)) {
      // for the gallery landing experiment we're pre-bucketing and redirecting to ?gallery
      // if it turns out to be an address page, we remove the param
      const url = locationDescriptorToString(keepBetaParam(location.pathname), createHref, location)
      replace(url)
    }
  }, [])

  useEffect(() => {
    // redirect legacy URLs with hash to the new URL with query params
    if (hash) {
      const searchParams = new URLSearchParams(window.location.search)
      searchParams.set('listing_id', hash)
      dispatch(deleteHash())
      dispatch(replaceSearch({ search: searchParams.toString() }))
    }
  }, [hash])

  const context = useMemo(
    (): AddressPageContextType => ({
      DetailAds,
      DetailAppBanner,
      FavoriteContainer,
      getImagePlaceholder,
      getRootRelativeUrlFromFiltersAndLocation,
      handleBackBtnClick,
      hoodAndCityLinks,
      keepBetaParam,
      mapBoxBrowserKey: MAPBOX_BROWSER_KEY ?? '',
      MessageButton: shouldLimitLeads ? forwardRef(() => null) : AddressMessageButton,
      mixpanelEvent: (...args: Parameters<typeof mixpanelEvent>) =>
        dispatch(mixpanelEvent(...args)),
      Navbar,
      NotifyMeButton,
      InactiveSaveSearchSection,
      preloadRoutes,
      pushSearch,
      Routes: routesConstants,
      selectDisplayMedia,
      selectIsMessaged,
      selectListingId,
      TourButton: shouldLimitLeads ? () => null : AddressTourButton,
      UnitsTableContainer: NZRUnitsTableLazyLoadableSection,
      useStreamChannel,
    }),
    [dispatch, selectIsMessaged, shouldLimitLeads]
  )

  return (
    <AddressPageContext.Provider value={context}>
      <PageMultiUnitAddressRoot>
        <ManageTitle makeTitle={addressPageTitle} titleProps={titleProps} />
        <BottomHalf />
      </PageMultiUnitAddressRoot>
    </AddressPageContext.Provider>
  )
}
